window.MioCollectorReviews = window.MioCollectorReviews || {};
window.MioCollectorReviews.initBlock = function initBlock() {
    class CollectorReviewsBanner extends HTMLElement {
      connectedCallback() {
        if (this.collectorReviewsReady) return;
        this.collectorReviewsReady = true;

        this.track = this.querySelector('.collector-reviews-banner__track');
        this.scroller = this.querySelector('[data-collector-reviews-scroller]');
        this.originalCards = Array.from(this.querySelectorAll('[data-collector-review-card]:not([data-collector-review-clone])'));
        this.prevButton = this.querySelector('[data-collector-reviews-prev]');
        this.nextButton = this.querySelector('[data-collector-reviews-next]');
        this.dotsContainer = this.querySelector('[data-collector-reviews-dots]');

        if (!this.track || !this.scroller || !this.originalCards.length || !this.prevButton || !this.nextButton) {
          this.collectorReviewsReady = false;
          return;
        }

        this.currentIndex = 0;
        this.virtualIndex = 0;
        this.focusCount = 1;
        this.peekRatio = 0.18;
        this.gap = 16;
        this.cardWidth = 0;
        this.baseDevicePixelRatio = window.devicePixelRatio || 1;
        this.transitionTimer = null;
        this.activePointerId = null;
        this.isPointerDown = false;
        this.isDragging = false;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.dragLastX = 0;
        this.dragLastY = 0;
        this.dragStartTime = 0;
        this.dragAnimationFrame = null;
        this.queuedDragX = 0;
        this.currentDragX = 0;
        this.activeTouchId = null;
        this.useTouchFallback = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
        this.pointerCancelCount = 0;
        this.touchCancelCount = 0;
        this.lastReleaseVelocity = 0;
        this.lastSwipeReason = 'init';
        this.debugCounts = {
          pointerdown: 0,
          pointermove: 0,
          pointerup: 0,
          pointercancel: 0,
          touchstart: 0,
          touchmove: 0,
          touchend: 0,
          touchcancel: 0
        };
        this.debugSnapshot = {};
        this.debugEnabled = (() => {
          try {
            return new URLSearchParams(window.location.search).has('mioReviewsDebug');
          } catch (error) {
            return false;
          }
        })();
        this.debugOverlay = null;
        this.debugFrame = null;

        this.onResize = this.handleResize.bind(this);
        this.onKeydown = this.handleKeydown.bind(this);
        this.onTransitionEnd = this.handleTransitionEnd.bind(this);
        this.onPointerDown = this.handlePointerDown.bind(this);
        this.onPointerMove = this.handlePointerMove.bind(this);
        this.onPointerUp = this.handlePointerUp.bind(this);
        this.onPointerCancel = this.handlePointerCancel.bind(this);
        this.onTouchStart = this.handleTouchStart.bind(this);
        this.onTouchMove = this.handleTouchMove.bind(this);
        this.onTouchEnd = this.handleTouchEnd.bind(this);
        this.onTouchCancel = this.handleTouchCancel.bind(this);
        this.onPrevClick = () => this.step(-1);
        this.onNextClick = () => this.step(1);

        this.prevButton.addEventListener('click', this.onPrevClick);
        this.nextButton.addEventListener('click', this.onNextClick);
        this.scroller.addEventListener('keydown', this.onKeydown);
        this.scroller.addEventListener('pointerdown', this.onPointerDown);
        this.scroller.addEventListener('pointermove', this.onPointerMove);
        this.scroller.addEventListener('pointerup', this.onPointerUp);
        this.scroller.addEventListener('pointercancel', this.onPointerCancel);
        this.scroller.addEventListener('lostpointercapture', this.onPointerCancel);
        if (this.useTouchFallback) {
          this.scroller.addEventListener('touchstart', this.onTouchStart, { passive: true });
          this.scroller.addEventListener('touchmove', this.onTouchMove, { passive: false });
          this.scroller.addEventListener('touchend', this.onTouchEnd, { passive: true });
          this.scroller.addEventListener('touchcancel', this.onTouchCancel, { passive: true });
        }
        this.track.addEventListener('transitionend', this.onTransitionEnd);
        window.addEventListener('resize', this.onResize);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.onResize);
          this.resizeObserver.observe(this.scroller);
        }

        this.buildLoop();
        this.measure();
        this.renderDots();
        this.goTo(0, false);
      }

      disconnectedCallback() {
        this.prevButton?.removeEventListener('click', this.onPrevClick);
        this.nextButton?.removeEventListener('click', this.onNextClick);
        this.scroller?.removeEventListener('keydown', this.onKeydown);
        this.scroller?.removeEventListener('pointerdown', this.onPointerDown);
        this.scroller?.removeEventListener('pointermove', this.onPointerMove);
        this.scroller?.removeEventListener('pointerup', this.onPointerUp);
        this.scroller?.removeEventListener('pointercancel', this.onPointerCancel);
        this.scroller?.removeEventListener('lostpointercapture', this.onPointerCancel);
        this.scroller?.removeEventListener('touchstart', this.onTouchStart);
        this.scroller?.removeEventListener('touchmove', this.onTouchMove);
        this.scroller?.removeEventListener('touchend', this.onTouchEnd);
        this.scroller?.removeEventListener('touchcancel', this.onTouchCancel);
        this.track?.removeEventListener('transitionend', this.onTransitionEnd);
        window.removeEventListener('resize', this.onResize);
        this.resizeObserver?.disconnect();
        if (this.dragAnimationFrame !== null) {
          window.cancelAnimationFrame(this.dragAnimationFrame);
        }
        if (this.debugFrame !== null) {
          window.cancelAnimationFrame(this.debugFrame);
          this.debugFrame = null;
        }
        this.debugOverlay?.remove();
        this.debugOverlay = null;
        window.clearTimeout(this.transitionTimer);
        this.collectorReviewsReady = false;
      }

      buildLoop() {
        this.track.querySelectorAll('[data-collector-review-clone]').forEach((clone) => clone.remove());

        const beforeFragment = document.createDocumentFragment();
        const afterFragment = document.createDocumentFragment();

        this.originalCards.forEach((card) => {
          const beforeClone = card.cloneNode(true);
          const afterClone = card.cloneNode(true);

          beforeClone.setAttribute('data-collector-review-clone', 'true');
          afterClone.setAttribute('data-collector-review-clone', 'true');
          beforeClone.setAttribute('aria-hidden', 'true');
          afterClone.setAttribute('aria-hidden', 'true');
          beforeClone.setAttribute('inert', '');
          afterClone.setAttribute('inert', '');

          beforeFragment.appendChild(beforeClone);
          afterFragment.appendChild(afterClone);
        });

        this.track.insertBefore(beforeFragment, this.track.firstChild);
        this.track.appendChild(afterFragment);
        this.cards = Array.from(this.track.querySelectorAll('[data-collector-review-card]'));
        this.realStartIndex = this.originalCards.length;
      }

      getFocusCount() {
        const zoomFocusCount = this.getZoomFocusCount();
        const outerWidth = window.outerWidth || window.innerWidth || 0;

        if (zoomFocusCount < 4 && outerWidth >= 1024) return zoomFocusCount;
        if (window.matchMedia('(max-width: 749px)').matches) return 1;
        if (zoomFocusCount < 4) return zoomFocusCount;
        if (window.matchMedia('(max-width: 989px)').matches) return 2;
        if (window.matchMedia('(max-width: 1299px)').matches) return 3;
        return 4;
      }

      getZoomRatio() {
        const currentRatio = window.devicePixelRatio || this.baseDevicePixelRatio || 1;
        const baseRatio = this.baseDevicePixelRatio || currentRatio || 1;
        const deviceZoomRatio = currentRatio / baseRatio;
        const outerWidth = window.outerWidth || 0;
        const innerWidth = window.innerWidth || 0;
        const screenWidth = window.screen?.width || 0;
        const isTopLevelPage = window.top === window.self;
        const outerZoomRatio = isTopLevelPage && screenWidth >= 1024 && outerWidth >= 1024 && innerWidth > 0
          ? outerWidth / innerWidth
          : 1;

        return Math.max(1, deviceZoomRatio, outerZoomRatio);
      }

      getZoomFocusCount() {
        const zoomRatio = this.getZoomRatio();

        if (zoomRatio >= 2.75) return 1;
        if (zoomRatio >= 1.85) return 2;
        if (zoomRatio >= 1.35) return 3;

        return 4;
      }

      getPeekRatio() {
        if (window.matchMedia('(max-width: 749px)').matches) return 0.14;
        if (window.matchMedia('(max-width: 989px)').matches) return 0.34;
        if (window.matchMedia('(max-width: 1279px)').matches) return 0.46;
        const compactDesktop = window.MioViewportLayout?.matches
          ? window.MioViewportLayout.matches({ minWidth: 1500, maxWidth: 2100, maxHeight: 1020 })
          : window.matchMedia('(min-width: 1500px) and (max-width: 2100px) and (max-height: 1020px)').matches;
        if (compactDesktop) return 0.1;
        return 0.68;
      }

      getMinCardWidth() {
        if (window.matchMedia('(max-width: 749px)').matches) return 286;
        if (window.matchMedia('(max-width: 989px)').matches) return 238;
        if (window.matchMedia('(max-width: 1279px)').matches) return 255;
        return 274;
      }

      getReadableLayout(visibleWidth, focusCount, minCardWidth) {
        let nextPeekRatio = visibleWidth < 260 ? 0 : this.getPeekRatio();
        let gapCount = nextPeekRatio > 0 ? focusCount + 1 : Math.max(focusCount - 1, 0);
        let usableWidth = Math.max(0, visibleWidth - (this.gap * gapCount));
        let visibleCardUnits = focusCount + (nextPeekRatio * 2);
        let nextCardWidth = visibleCardUnits > 0 ? usableWidth / visibleCardUnits : 0;

        if (nextPeekRatio > 0 && nextCardWidth < minCardWidth) {
          const maxPeekRatio = Math.max(0, ((usableWidth / minCardWidth) - focusCount) / 2);
          nextPeekRatio = Math.min(nextPeekRatio, maxPeekRatio);
          gapCount = nextPeekRatio > 0 ? focusCount + 1 : Math.max(focusCount - 1, 0);
          usableWidth = Math.max(0, visibleWidth - (this.gap * gapCount));
          visibleCardUnits = focusCount + (nextPeekRatio * 2);
          nextCardWidth = visibleCardUnits > 0 ? usableWidth / visibleCardUnits : 0;
        }

        return {
          focusCount,
          peekRatio: nextPeekRatio,
          cardWidth: Math.max(0, nextCardWidth)
        };
      }

      measure() {
        const styles = window.getComputedStyle(this.track);
        this.gap = parseFloat(styles.columnGap || styles.gap) || 0;

        const visibleWidth = this.scroller.clientWidth;
        const minCardWidth = this.getMinCardWidth();
        const maxFocusCount = Math.min(this.getFocusCount(), this.getZoomFocusCount(), this.originalCards.length);
        let nextLayout = null;

        for (let focusCandidate = maxFocusCount; focusCandidate >= 1; focusCandidate -= 1) {
          const candidateLayout = this.getReadableLayout(visibleWidth, focusCandidate, minCardWidth);

          if (candidateLayout.cardWidth >= minCardWidth - 0.5 || focusCandidate === 1) {
            nextLayout = candidateLayout;
            break;
          }
        }

        this.focusCount = nextLayout.focusCount;
        this.peekRatio = nextLayout.peekRatio;
        this.cardWidth = nextLayout.cardWidth;

        this.style.setProperty('--crb-card-width', `${this.cardWidth}px`);
        this.style.setProperty('--crb-quote-font-size', `${this.getScaledTextSize('quote', this.focusCount)}rem`);
        this.style.setProperty('--crb-tag-font-size', `${this.getScaledTextSize('tag', this.focusCount)}rem`);
        this.style.setProperty('--crb-product-name-font-size', `${this.getScaledTextSize('productName', this.focusCount)}rem`);
        this.style.setProperty('--crb-product-brand-font-size', `${this.getScaledTextSize('productBrand', this.focusCount)}rem`);
        this.style.setProperty('--crb-attribution-font-size', `${this.getScaledTextSize('attribution', this.focusCount)}rem`);
        this.dataset.focusCount = String(this.focusCount);
      }

      getTextScale(focusCount) {
        const zoomRatio = this.getZoomRatio();
        let scale = 1;

        if (zoomRatio >= 2.75) {
          scale = 0.78;
        } else if (zoomRatio >= 1.85) {
          scale = 0.84;
        } else if (zoomRatio >= 1.35) {
          scale = 0.9;
        } else if (focusCount <= 1) {
          scale = 0.88;
        } else if (focusCount === 2) {
          scale = 0.92;
        } else if (focusCount === 3) {
          scale = 0.96;
        }

        return scale;
      }

      getBaseTextSize(type) {
        const rootFontSize = parseFloat(window.getComputedStyle(document.documentElement).fontSize) || 10;
        const viewportWidth = window.innerWidth || this.scroller.clientWidth || 0;

        if (window.matchMedia('(max-width: 749px)').matches) {
          return {
            quote: 1.4,
            tag: 1.119,
            productName: 1.32,
            productBrand: 1.16,
            attribution: 1.16
          }[type];
        }

        if (type === 'quote' && window.matchMedia('(max-width: 989px)').matches) {
          return 1.5;
        }

        return {
          quote: Math.max(1.52, Math.min((viewportWidth * 0.009) / rootFontSize, 1.76)),
          tag: Math.max(1.114, Math.min(((viewportWidth * 0.0024024) / rootFontSize) + 1.049, 1.31)),
          productName: 1.46,
          productBrand: 1.28,
          attribution: 1.25
        }[type];
      }

      getScaledTextSize(type, focusCount) {
        const scaledSize = this.getBaseTextSize(type) * this.getTextScale(focusCount);

        return Math.round(scaledSize * 1000) / 1000;
      }

      renderDots() {
        if (!this.dotsContainer || this.dotsContainer.hasAttribute('hidden')) return;

        this.dotsContainer.innerHTML = '';
        const dotCount = this.originalCards.length;

        for (let index = 0; index < dotCount; index += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'collector-reviews-banner__dot';
          dot.setAttribute('aria-label', `Go to review ${index + 1}`);
          dot.setAttribute('aria-controls', this.scroller.id);
          dot.addEventListener('click', () => this.goTo(index));
          this.dotsContainer.appendChild(dot);
        }
      }

      getNormalizedIndex(index) {
        const count = this.originalCards.length;
        return ((index % count) + count) % count;
      }

      getTrackOffset(virtualIndex) {
        const baseIndex = this.realStartIndex + virtualIndex;
        const previousIndex = baseIndex - 1;
        const cardStep = this.cardWidth + this.gap;

        if (this.peekRatio === 0) return baseIndex * cardStep;

        return (previousIndex * cardStep) + ((1 - this.peekRatio) * this.cardWidth);
      }

      clampNumber(value, min, max) {
        return Math.min(max, Math.max(min, value));
      }

      isMobileLayout() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      getCardPitch() {
        return this.cardWidth + this.gap;
      }

      getIntentThreshold() {
        return this.isMobileLayout() ? 6 : 8;
      }

      getHorizontalIntentRatio() {
        return this.isMobileLayout() ? 1.08 : 1.25;
      }

      getFirstStepThreshold() {
        const pitch = this.getCardPitch();

        if (this.isMobileLayout()) {
          return this.clampNumber(pitch * 0.12, 24, 38);
        }

        return this.clampNumber(pitch * 0.18, 28, 72);
      }

      getFlickVelocityThreshold() {
        return this.isMobileLayout() ? 0.34 : 0.48;
      }

      getSecondStepThreshold() {
        const pitch = this.getCardPitch();

        if (this.isMobileLayout()) {
          return this.clampNumber(pitch * 0.9, 210, 320);
        }

        return this.clampNumber(pitch * 1.08, 310, 390);
      }

      getFastTwoStepVelocityThreshold() {
        return this.isMobileLayout() ? 1.15 : 1.75;
      }

      getFastTwoStepDistanceThreshold() {
        return this.getCardPitch() * (this.isMobileLayout() ? 0.48 : 0.72);
      }

      describeTarget(target) {
        if (!(target instanceof Element)) return 'unknown';

        const className = typeof target.className === 'string'
          ? target.className.trim().replace(/\s+/g, '.')
          : '';

        return `${target.tagName.toLowerCase()}${className ? `.${className}` : ''}`.slice(0, 92);
      }

      getComposedPathSummary(event) {
        if (!event || typeof event.composedPath !== 'function') return '-';

        return event.composedPath()
          .filter((item) => item instanceof Element)
          .slice(0, 5)
          .map((item) => this.describeTarget(item))
          .join(' > ');
      }

      getTouchActionValue(target = this.scroller) {
        if (!(target instanceof Element)) return '-';

        try {
          return window.getComputedStyle(target).touchAction || '-';
        } catch (error) {
          return '-';
        }
      }

      getCurrentTransformValue() {
        try {
          return window.getComputedStyle(this.track).transform || 'none';
        } catch (error) {
          return 'unknown';
        }
      }

      getTrackedTouch(touchList) {
        if (this.activeTouchId === null) return null;

        for (let index = 0; index < touchList.length; index += 1) {
          if (touchList[index].identifier === this.activeTouchId) {
            return touchList[index];
          }
        }

        return null;
      }

      updateDebugOverlay(extra = {}) {
        if (!this.debugEnabled) return;

        Object.assign(this.debugSnapshot, extra);

        if (this.debugFrame !== null) return;

        this.debugFrame = window.requestAnimationFrame(() => {
          this.debugFrame = null;

          if (!this.debugOverlay) {
            this.debugOverlay = document.createElement('div');
            this.debugOverlay.setAttribute('aria-hidden', 'true');
            this.debugOverlay.style.cssText = [
              'position:fixed',
              'left:0',
              'right:0',
              'bottom:0',
              'z-index:2147483647',
              'max-height:42vh',
              'overflow:auto',
              'padding:8px 10px',
              'background:rgba(0,0,0,0.82)',
              'color:#fff',
              'font:11px/1.35 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace',
              'white-space:pre-wrap',
              'pointer-events:none'
            ].join(';');
            document.body.appendChild(this.debugOverlay);
          }

          const pitch = this.getCardPitch();
          const threshold = this.getFirstStepThreshold();
          const snapshot = this.debugSnapshot;
          const currentTransform = this.getCurrentTransformValue();
          const previousTransform = snapshot.previousTransform || '-';
          const didTransformChange = previousTransform !== '-' && previousTransform !== currentTransform;

          this.debugOverlay.textContent = [
            'mio-reviews-debug-2026-05-18-b',
            `pd=${this.debugCounts.pointerdown} pm=${this.debugCounts.pointermove} pu=${this.debugCounts.pointerup} pc=${this.debugCounts.pointercancel}`,
            `ts=${this.debugCounts.touchstart} tm=${this.debugCounts.touchmove} te=${this.debugCounts.touchend} tc=${this.debugCounts.touchcancel}`,
            `viewport=${window.innerWidth} scroller=${this.scroller.clientWidth} card=${Math.round(this.cardWidth)} gap=${Math.round(this.gap)} pitch=${Math.round(pitch)}`,
            `index=${this.currentIndex} virtual=${this.virtualIndex} target=${snapshot.targetIndex ?? '-'} cards=${this.originalCards.length} clones=${this.cards.length - this.originalCards.length}`,
            `target=${snapshot.target ?? '-'} path=${snapshot.path ?? '-'}`,
            `input=${snapshot.pointerType ?? '-'} startX=${Math.round(this.dragStartX || 0)} currentX=${Math.round(this.dragLastX || 0)} dx=${Math.round(snapshot.deltaX ?? 0)} dy=${Math.round(snapshot.deltaY ?? 0)}`,
            `abs=${Math.round(Math.abs(snapshot.deltaX ?? 0))}/${Math.round(Math.abs(snapshot.deltaY ?? 0))} velocity=${(snapshot.velocity ?? this.lastReleaseVelocity).toFixed(3)}`,
            `threshold=${Math.round(threshold)}px pitch%=${pitch ? Math.round((threshold / pitch) * 100) : 0}% viewport%=${this.scroller.clientWidth ? Math.round((threshold / this.scroller.clientWidth) * 100) : 0}%`,
            `thresholdCrossed=${snapshot.thresholdCrossed ?? false} horizontal=${snapshot.horizontalIntent ?? this.isDragging} verticalWon=${snapshot.verticalIntent ?? false}`,
            `transitionDisabled=${this.isDragging} touchAction=${snapshot.touchAction ?? this.getTouchActionValue()} preventDefault=${snapshot.preventDefaultAttempted ?? false}`,
            `cancelable=${snapshot.cancelable ?? '-'} passiveSuspect=${snapshot.passiveProblemSuspected ?? false}`,
            `transform=${currentTransform} previous=${previousTransform} changed=${didTransformChange}`,
            `pointercancel=${this.pointerCancelCount} touchcancel=${this.touchCancelCount} reason=${snapshot.reason ?? this.lastSwipeReason}`
          ].join('\n');
        });
      }

      setTrackPosition(virtualIndex, animate = true) {
        this.virtualIndex = virtualIndex;
        this.track.style.transition = animate ? '' : 'none';
        this.style.setProperty('--crb-track-x', `${this.getTrackOffset(virtualIndex)}px`);

        if (!animate) {
          this.track.getBoundingClientRect();
          this.track.style.transition = '';
        }

        this.updateState(virtualIndex);
        this.updateDebugOverlay({ targetIndex: this.getNormalizedIndex(virtualIndex) });
      }

      step(direction) {
        if (!direction || this.originalCards.length <= 1) return;
        this.goTo(this.currentIndex + direction);
      }

      goTo(index, animate = true) {
        window.clearTimeout(this.transitionTimer);
        this.currentIndex = this.getNormalizedIndex(index);
        this.setTrackPosition(index, animate);

        this.transitionTimer = window.setTimeout(() => {
          this.normalizeLoopPosition();
        }, animate ? 460 : 0);
      }

      normalizeLoopPosition() {
        if (this.virtualIndex === this.currentIndex) return;
        this.setTrackPosition(this.currentIndex, false);
      }

      handleTransitionEnd(event) {
        if (event.target !== this.track || event.propertyName !== 'transform') return;
        this.normalizeLoopPosition();
      }

      handleResize() {
        this.measure();
        this.setTrackPosition(this.currentIndex, false);
      }

      handlePointerDown(event) {
        if (this.originalCards.length <= 1) return;
        if (event.button !== undefined && event.button !== 0) return;
        if (event.target instanceof Element && event.target.closest('a, button, input, textarea, select, summary, [role="button"]')) return;
        this.debugCounts.pointerdown += 1;

        if (event.pointerType === 'touch' && this.useTouchFallback) {
          this.lastSwipeReason = 'pointer-touch-deferred-to-touch-events';
          this.updateDebugOverlay({
            pointerType: 'pointer-touch',
            target: this.describeTarget(event.target),
            path: this.getComposedPathSummary(event),
          touchAction: this.getTouchActionValue(event.target),
          cancelable: event.cancelable,
          preventDefaultAttempted: false,
          passiveProblemSuspected: false,
          reason: this.lastSwipeReason
        });
        return;
        }

        window.clearTimeout(this.transitionTimer);

        this.activePointerId = event.pointerId;
        this.isPointerDown = true;
        this.isDragging = false;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragLastX = event.clientX;
        this.dragLastY = event.clientY;
        this.dragStartTime = window.performance?.now ? window.performance.now() : Date.now();
        this.lastSwipeReason = 'pointer-down';
        this.updateDebugOverlay({
          pointerType: event.pointerType || 'unknown',
          target: this.describeTarget(event.target),
          path: this.getComposedPathSummary(event),
          touchAction: this.getTouchActionValue(event.target),
          cancelable: event.cancelable,
          preventDefaultAttempted: false,
          passiveProblemSuspected: false,
          thresholdCrossed: false,
          horizontalIntent: false,
          verticalIntent: false,
          deltaX: 0,
          deltaY: 0,
          reason: this.lastSwipeReason
        });
      }

      handlePointerMove(event) {
        if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;
        this.debugCounts.pointermove += 1;

        const deltaX = event.clientX - this.dragStartX;
        const deltaY = event.clientY - this.dragStartY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        const thresholdCrossed = absX >= this.getIntentThreshold() || absY >= this.getIntentThreshold();

        this.dragLastX = event.clientX;
        this.dragLastY = event.clientY;

        if (!this.isDragging) {
          const intentThreshold = this.getIntentThreshold();

          if (absX < intentThreshold && absY < intentThreshold) {
            this.updateDebugOverlay({
              pointerType: event.pointerType || 'unknown',
              deltaX,
              deltaY,
              thresholdCrossed,
              horizontalIntent: false,
              verticalIntent: false,
              cancelable: event.cancelable,
              reason: 'below-intent-threshold'
            });
            return;
          }

          if (absY > absX) {
            this.lastSwipeReason = 'vertical-intent';
            this.updateDebugOverlay({
              pointerType: event.pointerType || 'unknown',
              deltaX,
              deltaY,
              thresholdCrossed,
              horizontalIntent: false,
              verticalIntent: true,
              cancelable: event.cancelable,
              reason: this.lastSwipeReason
            });
            this.cancelDrag();
            return;
          }

          if (absX <= absY * this.getHorizontalIntentRatio()) {
            this.updateDebugOverlay({
              pointerType: event.pointerType || 'unknown',
              deltaX,
              deltaY,
              thresholdCrossed,
              horizontalIntent: false,
              verticalIntent: false,
              cancelable: event.cancelable,
              reason: 'waiting-for-horizontal-intent'
            });
            return;
          }

          this.isDragging = true;
          this.scroller.classList.add('is-dragging');
          this.lastSwipeReason = 'dragging';

          if (this.scroller.setPointerCapture) {
            this.scroller.setPointerCapture(event.pointerId);
          }
        }

        if (!this.isDragging) return;

        const previousTransform = this.getCurrentTransformValue();
        event.preventDefault();

        const dragLimit = Math.min(Math.max(this.cardWidth * 0.7, 90), 220);
        const dampedDelta = Math.max(-dragLimit, Math.min(deltaX, dragLimit));
        this.queueDragOffset(dampedDelta);
        this.updateDebugOverlay({
          pointerType: event.pointerType || 'unknown',
          deltaX,
          deltaY,
          thresholdCrossed,
          horizontalIntent: true,
          verticalIntent: false,
          previousTransform,
          preventDefaultAttempted: true,
          cancelable: event.cancelable,
          passiveProblemSuspected: false,
          reason: 'dragging'
        });
      }

      handlePointerUp(event) {
        if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;
        this.debugCounts.pointerup += 1;

        const wasDragging = this.isDragging;
        this.flushDragOffset();

        const deltaX = this.dragLastX - this.dragStartX;
        const elapsed = Math.max(1, (window.performance?.now ? window.performance.now() : Date.now()) - this.dragStartTime);
        const velocity = deltaX / elapsed;
        this.lastReleaseVelocity = velocity;
        const stepCount = wasDragging ? this.getDragStepCount(deltaX, velocity) : 0;

        this.endDrag(event.pointerId, { removeDraggingClass: false, resetOffset: false });

        if (!wasDragging) {
          this.scroller.classList.remove('is-dragging');
          this.resetDragOffset();
          return;
        }

        event.preventDefault();

        const targetIndex = stepCount > 0 ? this.currentIndex + (deltaX < 0 ? stepCount : -stepCount) : this.currentIndex;
        this.updateDebugOverlay({
          pointerType: event.pointerType || 'unknown',
          deltaX,
          deltaY: this.dragLastY - this.dragStartY,
          velocity,
          targetIndex: this.getNormalizedIndex(targetIndex),
          horizontalIntent: true,
          reason: this.lastSwipeReason
        });
        this.releaseDragTo(targetIndex);
      }

      handlePointerCancel(event) {
        if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;
        const wasDragging = this.isDragging;
        this.debugCounts.pointercancel += 1;
        this.pointerCancelCount += 1;
        this.lastSwipeReason = 'pointer-cancel';
        this.updateDebugOverlay({
          pointerType: event.pointerType || 'unknown',
          cancelable: event.cancelable,
          reason: this.lastSwipeReason
        });

        this.flushDragOffset();
        this.endDrag(event.pointerId, { removeDraggingClass: false, resetOffset: false });

        if (wasDragging) {
          this.releaseDragTo(this.currentIndex);
        } else {
          this.scroller.classList.remove('is-dragging');
          this.resetDragOffset();
        }
      }

      handleTouchStart(event) {
        this.debugCounts.touchstart += 1;

        if (this.originalCards.length <= 1 || event.touches.length !== 1) {
          this.lastSwipeReason = 'touchstart-ignored';
          this.updateDebugOverlay({
            pointerType: 'touch',
            target: this.describeTarget(event.target),
            path: this.getComposedPathSummary(event),
            touchAction: this.getTouchActionValue(event.target),
            cancelable: event.cancelable,
            preventDefaultAttempted: false,
            passiveProblemSuspected: false,
            reason: this.lastSwipeReason
          });
          return;
        }

        if (event.target instanceof Element && event.target.closest('a, button, input, textarea, select, summary, [role="button"]')) return;

        const touch = event.changedTouches[0];
        if (!touch) return;

        window.clearTimeout(this.transitionTimer);

        this.activeTouchId = touch.identifier;
        this.activePointerId = `touch:${touch.identifier}`;
        this.isPointerDown = true;
        this.isDragging = false;
        this.dragStartX = touch.clientX;
        this.dragStartY = touch.clientY;
        this.dragLastX = touch.clientX;
        this.dragLastY = touch.clientY;
        this.dragStartTime = window.performance?.now ? window.performance.now() : Date.now();
        this.lastSwipeReason = 'touchstart';
        this.updateDebugOverlay({
          pointerType: 'touch',
          target: this.describeTarget(event.target),
          path: this.getComposedPathSummary(event),
          touchAction: this.getTouchActionValue(event.target),
          cancelable: event.cancelable,
          preventDefaultAttempted: false,
          passiveProblemSuspected: false,
          thresholdCrossed: false,
          horizontalIntent: false,
          verticalIntent: false,
          deltaX: 0,
          deltaY: 0,
          reason: this.lastSwipeReason
        });
      }

      handleTouchMove(event) {
        if (!this.isPointerDown || this.activeTouchId === null) return;

        const touch = this.getTrackedTouch(event.touches);
        if (!touch) return;

        this.debugCounts.touchmove += 1;

        const deltaX = touch.clientX - this.dragStartX;
        const deltaY = touch.clientY - this.dragStartY;
        const absX = Math.abs(deltaX);
        const absY = Math.abs(deltaY);
        const thresholdCrossed = absX >= this.getIntentThreshold() || absY >= this.getIntentThreshold();

        this.dragLastX = touch.clientX;
        this.dragLastY = touch.clientY;

        if (!this.isDragging) {
          const intentThreshold = this.getIntentThreshold();

          if (absX < intentThreshold && absY < intentThreshold) {
            this.updateDebugOverlay({
              pointerType: 'touch',
              deltaX,
              deltaY,
              thresholdCrossed,
              horizontalIntent: false,
              verticalIntent: false,
              cancelable: event.cancelable,
              reason: 'touch-below-intent-threshold'
            });
            return;
          }

          if (absY > absX) {
            this.lastSwipeReason = 'touch-vertical-intent';
            this.updateDebugOverlay({
              pointerType: 'touch',
              deltaX,
              deltaY,
              thresholdCrossed,
              horizontalIntent: false,
              verticalIntent: true,
              cancelable: event.cancelable,
              reason: this.lastSwipeReason
            });
            this.cancelDrag();
            return;
          }

          if (absX <= absY * this.getHorizontalIntentRatio()) {
            this.updateDebugOverlay({
              pointerType: 'touch',
              deltaX,
              deltaY,
              thresholdCrossed,
              horizontalIntent: false,
              verticalIntent: false,
              cancelable: event.cancelable,
              reason: 'touch-waiting-for-horizontal-intent'
            });
            return;
          }

          this.isDragging = true;
          this.scroller.classList.add('is-dragging');
          this.lastSwipeReason = 'touch-dragging';
        }

        if (!this.isDragging) return;

        const previousTransform = this.getCurrentTransformValue();
        let preventDefaultAttempted = false;
        if (event.cancelable) {
          event.preventDefault();
          preventDefaultAttempted = true;
        }

        const dragLimit = Math.min(Math.max(this.cardWidth * 0.7, 90), 220);
        const dampedDelta = Math.max(-dragLimit, Math.min(deltaX, dragLimit));
        this.queueDragOffset(dampedDelta);
        this.updateDebugOverlay({
          pointerType: 'touch',
          deltaX,
          deltaY,
          thresholdCrossed,
          horizontalIntent: true,
          verticalIntent: false,
          previousTransform,
          preventDefaultAttempted,
          cancelable: event.cancelable,
          passiveProblemSuspected: !event.cancelable,
          reason: this.lastSwipeReason
        });
      }

      handleTouchEnd(event) {
        if (!this.isPointerDown || this.activeTouchId === null) return;

        const touch = this.getTrackedTouch(event.changedTouches);
        if (!touch) return;

        this.debugCounts.touchend += 1;
        this.dragLastX = touch.clientX;
        this.dragLastY = touch.clientY;

        const wasDragging = this.isDragging;
        this.flushDragOffset();

        const deltaX = this.dragLastX - this.dragStartX;
        const elapsed = Math.max(1, (window.performance?.now ? window.performance.now() : Date.now()) - this.dragStartTime);
        const velocity = deltaX / elapsed;
        this.lastReleaseVelocity = velocity;
        const stepCount = wasDragging ? this.getDragStepCount(deltaX, velocity) : 0;

        this.endDrag(null, { removeDraggingClass: false, resetOffset: false });

        if (!wasDragging) {
          this.scroller.classList.remove('is-dragging');
          this.resetDragOffset();
          return;
        }

        const targetIndex = stepCount > 0 ? this.currentIndex + (deltaX < 0 ? stepCount : -stepCount) : this.currentIndex;
        this.updateDebugOverlay({
          pointerType: 'touch',
          deltaX,
          deltaY: this.dragLastY - this.dragStartY,
          velocity,
          targetIndex: this.getNormalizedIndex(targetIndex),
          horizontalIntent: true,
          cancelable: event.cancelable,
          reason: this.lastSwipeReason
        });
        this.releaseDragTo(targetIndex);
      }

      handleTouchCancel(event) {
        if (!this.isPointerDown || this.activeTouchId === null) return;

        const touch = this.getTrackedTouch(event.changedTouches);
        if (!touch) return;

        const wasDragging = this.isDragging;
        this.debugCounts.touchcancel += 1;
        this.touchCancelCount += 1;
        this.lastSwipeReason = 'touchcancel';

        this.flushDragOffset();
        this.endDrag(null, { removeDraggingClass: false, resetOffset: false });

        this.updateDebugOverlay({
          pointerType: 'touch',
          cancelable: event.cancelable,
          reason: this.lastSwipeReason
        });

        if (wasDragging) {
          this.releaseDragTo(this.currentIndex);
        } else {
          this.scroller.classList.remove('is-dragging');
          this.resetDragOffset();
        }
      }

      cancelDrag() {
        this.isPointerDown = false;
        this.isDragging = false;
        this.activePointerId = null;
        this.activeTouchId = null;
        this.activeTouchId = null;
        this.resetDragOffset();
        this.scroller.classList.remove('is-dragging');
      }

      getDragStepCount(deltaX, velocity) {
        const absDelta = Math.abs(deltaX);
        const absVelocity = Math.abs(velocity);
        const firstStepThreshold = this.getFirstStepThreshold();
        const flickVelocityThreshold = this.getFlickVelocityThreshold();
        const secondStepThreshold = this.getSecondStepThreshold();

        if (absDelta < firstStepThreshold && absVelocity < flickVelocityThreshold) {
          this.lastSwipeReason = `no-advance: ${Math.round(absDelta)}px < ${Math.round(firstStepThreshold)}px, ${absVelocity.toFixed(2)} < ${flickVelocityThreshold}`;
          return 0;
        }

        const canSkipTwo = this.originalCards.length > 2;
        const isDeliberateLongSwipe = absDelta >= secondStepThreshold;
        const isFastCommittedSwipe = absVelocity >= this.getFastTwoStepVelocityThreshold() && absDelta >= this.getFastTwoStepDistanceThreshold();

        if (canSkipTwo && (isDeliberateLongSwipe || isFastCommittedSwipe)) {
          this.lastSwipeReason = isDeliberateLongSwipe ? 'advance-2: long-swipe' : 'advance-2: fast-swipe';
          return 2;
        }

        this.lastSwipeReason = absVelocity >= flickVelocityThreshold && absDelta < firstStepThreshold
          ? 'advance-1: fast-flick'
          : 'advance-1: distance';
        return 1;
      }

      queueDragOffset(offset) {
        this.queuedDragX = offset;

        if (this.dragAnimationFrame !== null) return;

        this.dragAnimationFrame = window.requestAnimationFrame(() => {
          this.dragAnimationFrame = null;
          this.currentDragX = this.queuedDragX;
          this.style.setProperty('--crb-drag-x', `${this.currentDragX}px`);
        });
      }

      flushDragOffset() {
        if (this.dragAnimationFrame !== null) {
          window.cancelAnimationFrame(this.dragAnimationFrame);
          this.dragAnimationFrame = null;
        }

        this.currentDragX = this.queuedDragX;
        this.style.setProperty('--crb-drag-x', `${this.currentDragX}px`);
      }

      resetDragOffset() {
        if (this.dragAnimationFrame !== null) {
          window.cancelAnimationFrame(this.dragAnimationFrame);
          this.dragAnimationFrame = null;
        }

        this.queuedDragX = 0;
        this.currentDragX = 0;
        this.style.setProperty('--crb-drag-x', '0px');
      }

      releaseDragTo(index) {
        this.track.getBoundingClientRect();
        this.scroller.classList.remove('is-dragging');
        this.resetDragOffset();

        if (index === this.currentIndex) {
          this.setTrackPosition(this.currentIndex);
        } else {
          this.goTo(index);
        }
      }

      endDrag(pointerId, options = {}) {
        const removeDraggingClass = options.removeDraggingClass !== false;
        const resetOffset = options.resetOffset !== false;
        const shouldReleasePointer = this.scroller.releasePointerCapture
          && pointerId !== null
          && this.scroller.hasPointerCapture?.(pointerId);

        this.isPointerDown = false;
        this.isDragging = false;
        this.activePointerId = null;

        if (resetOffset) {
          this.resetDragOffset();
        }

        if (removeDraggingClass) {
          this.scroller.classList.remove('is-dragging');
        }

        if (shouldReleasePointer) {
          this.scroller.releasePointerCapture(pointerId);
        }
      }

      handleKeydown(event) {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.step(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.step(1);
        }
      }

      updateState(virtualIndex = this.currentIndex) {
        const baseIndex = this.realStartIndex + virtualIndex;
        const peekBefore = baseIndex - 1;
        const peekAfter = baseIndex + this.focusCount;
        const hasPeeks = this.peekRatio > 0;

        this.cards.forEach((card, index) => {
          const isPeek = hasPeeks && (index === peekBefore || index === peekAfter);
          card.classList.toggle('is-peek', isPeek);
          card.classList.toggle('is-main', index >= baseIndex && index < baseIndex + this.focusCount);
        });

        this.prevButton.disabled = false;
        this.nextButton.disabled = false;

        if (this.dotsContainer && !this.dotsContainer.hasAttribute('hidden')) {
          Array.from(this.dotsContainer.children).forEach((dot, index) => {
            dot.classList.toggle('is-active', index === this.currentIndex);
            dot.setAttribute('aria-current', index === this.currentIndex ? 'true' : 'false');
          });
        }
      }
    }

    if (!customElements.get('collector-reviews-banner-v2')) {
      customElements.define('collector-reviews-banner-v2', CollectorReviewsBanner);
    }
  };

